import React, { useState, useEffect } from "react";
import { useDisplayConfig } from "../context/DisplayConfigContext";
import { useIp } from "../context/IpContext";

function nameToId(name) {
  return name
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9&-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const BLANK = {
  id: "",
  name: "",
  categories: [],
  showAll: false,
  ringEnabled: false,
};

export default function DisplaySettings() {
  const { displayConfigs, saveDisplayConfigs } = useDisplayConfig();
  const { ipAddress, port } = useIp();

  const [configs, setConfigs] = useState(displayConfigs ?? []);
  const [editingId, setEditingId] = useState(null); // display id, or "new"
  const [form, setForm] = useState(BLANK);
  const [catInput, setCatInput] = useState("");
  const [squareCats, setSquareCats] = useState([]);
  const [saving, setSaving] = useState(false);

  // Keep local configs in sync if the context updates (e.g. on first load)
  useEffect(() => {
    if (displayConfigs) setConfigs(displayConfigs);
  }, [displayConfigs]);

  // Fetch Square categories once so we can offer suggestions
  useEffect(() => {
    if (!ipAddress) return;
    fetch(`http://${ipAddress}:${port}/api/square-categories`)
      .then((r) => r.json())
      .then((data) => setSquareCats(data.categories ?? []))
      .catch(() => {});
  }, [ipAddress, port]);

  const startEdit = (display) => {
    setEditingId(display.id);
    setForm({ ...display, categories: [...display.categories] });
    setCatInput("");
  };

  const startAdd = () => {
    setEditingId("new");
    setForm({ ...BLANK, categories: [] });
    setCatInput("");
  };

  const cancel = () => {
    setEditingId(null);
    setForm(BLANK);
    setCatInput("");
  };

  const commitCategory = () => {
    const trimmed = catInput.trim();
    if (!trimmed || form.categories.includes(trimmed)) {
      setCatInput("");
      return;
    }
    setForm((prev) => ({ ...prev, categories: [...prev.categories, trimmed] }));
    setCatInput("");
  };

  const removeCategory = (cat) => {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.filter((c) => c !== cat),
    }));
  };

  const addSuggestion = (cat) => {
    if (form.categories.includes(cat)) return;
    setForm((prev) => ({ ...prev, categories: [...prev.categories, cat] }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const id =
      editingId === "new" ? nameToId(form.name) || form.name : editingId;
    const updated = { ...form, id };

    const newConfigs =
      editingId === "new"
        ? [...configs, updated]
        : configs.map((d) => (d.id === editingId ? updated : d));

    await saveDisplayConfigs(newConfigs);
    setConfigs(newConfigs);
    setSaving(false);
    cancel();
  };

  const handleDelete = async (id) => {
    const newConfigs = configs.filter((d) => d.id !== id);
    await saveDisplayConfigs(newConfigs);
    setConfigs(newConfigs);
    if (editingId === id) cancel();
  };

  // Square category suggestions filtered to what the user has typed
  const suggestions = squareCats.filter(
    (c) =>
      c.toLowerCase().includes(catInput.toLowerCase()) &&
      !form.categories.includes(c),
  );

  return (
    <div>
      {configs.map((display) => (
        <div key={display.id}>
          {/* Display row */}
          <div style={s.row}>
            <span style={s.displayName}>{display.name}</span>
            <div style={s.rowActions}>
              <button
                style={s.smallBtn}
                onClick={() =>
                  editingId === display.id ? cancel() : startEdit(display)
                }
              >
                {editingId === display.id ? "Cancel" : "Edit"}
              </button>
              <button
                style={{ ...s.smallBtn, ...s.deleteBtn }}
                onClick={() => handleDelete(display.id)}
              >
                ×
              </button>
            </div>
          </div>

          {/* Inline edit form */}
          {editingId === display.id && (
            <EditForm
              form={form}
              setForm={setForm}
              catInput={catInput}
              setCatInput={setCatInput}
              suggestions={suggestions}
              onCommitCat={commitCategory}
              onRemoveCat={removeCategory}
              onAddSuggestion={addSuggestion}
              onSave={handleSave}
              onCancel={cancel}
              saving={saving}
            />
          )}
        </div>
      ))}

      {/* Add display */}
      {editingId === "new" ? (
        <EditForm
          form={form}
          setForm={setForm}
          catInput={catInput}
          setCatInput={setCatInput}
          suggestions={suggestions}
          onCommitCat={commitCategory}
          onRemoveCat={removeCategory}
          onAddSuggestion={addSuggestion}
          onSave={handleSave}
          onCancel={cancel}
          saving={saving}
          isNew
        />
      ) : (
        <button style={s.addBtn} onClick={startAdd}>
          + Add Display
        </button>
      )}
    </div>
  );
}

function EditForm({
  form,
  setForm,
  catInput,
  setCatInput,
  suggestions,
  onCommitCat,
  onRemoveCat,
  onAddSuggestion,
  onSave,
  onCancel,
  saving,
  isNew,
}) {
  return (
    <div style={s.editBox}>
      {/* Name */}
      <div style={s.fieldRow}>
        <label style={s.fieldLabel}>Name</label>
        <input
          style={s.input}
          value={form.name}
          onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
          placeholder="e.g. Desserts"
        />
      </div>

      {/* Categories (only relevant if showAll is off) */}
      {!form.showAll && (
        <div style={s.fieldRow}>
          <label style={s.fieldLabel}>Categories</label>
          <div style={{ flex: 1 }}>
            {/* Existing category tags */}
            <div style={s.tagsRow}>
              {form.categories.map((cat) => (
                <span key={cat} style={s.tag}>
                  {cat}
                  <button
                    style={s.tagRemove}
                    onClick={() => onRemoveCat(cat)}
                    aria-label={`Remove ${cat}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>

            {/* Input to add a category */}
            <div style={{ display: "flex", gap: "4px", marginTop: "6px" }}>
              <input
                style={{ ...s.input, flex: 1 }}
                value={catInput}
                onChange={(e) => setCatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onCommitCat();
                  }
                }}
                placeholder="Type category name…"
              />
              <button style={s.smallBtn} onClick={onCommitCat}>
                Add
              </button>
            </div>

            {/* Suggestions from Square */}
            {catInput.trim() !== "" && suggestions.length > 0 && (
              <div style={s.suggestions}>
                {suggestions.slice(0, 6).map((cat) => (
                  <button
                    key={cat}
                    style={s.suggestionItem}
                    onClick={() => onAddSuggestion(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Show All toggle */}
      <div style={s.checkRow}>
        <label style={s.checkLabel}>
          <input
            type="checkbox"
            checked={form.showAll}
            onChange={(e) =>
              setForm((p) => ({ ...p, showAll: e.target.checked }))
            }
            style={{ marginRight: "6px" }}
          />
          Show all categories (receives every order item)
        </label>
      </div>

      {/* Ring Enabled toggle */}
      <div style={s.checkRow}>
        <label style={s.checkLabel}>
          <input
            type="checkbox"
            checked={form.ringEnabled}
            onChange={(e) =>
              setForm((p) => ({ ...p, ringEnabled: e.target.checked }))
            }
            style={{ marginRight: "6px" }}
          />
          Ring on new order
        </label>
      </div>

      {/* Save / Cancel */}
      <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
        <button
          style={{ ...s.smallBtn, flex: 1 }}
          onClick={onSave}
          disabled={saving || !form.name.trim()}
        >
          {saving ? "Saving…" : isNew ? "Add" : "Save"}
        </button>
        <button style={{ ...s.smallBtn, flex: 1 }} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

const s = {
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 0",
    borderBottom: "1px dashed #c9a84c",
  },
  displayName: {
    fontWeight: "bold",
    fontSize: "0.85rem",
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: "#3a3a3a",
  },
  rowActions: {
    display: "flex",
    gap: "6px",
  },
  smallBtn: {
    padding: "4px 10px",
    border: "2px solid #c9a84c",
    borderRadius: "3px",
    background: "#fec871",
    fontFamily: "'Courier New', Courier, monospace",
    fontWeight: "bold",
    fontSize: "0.75rem",
    letterSpacing: "0.5px",
    cursor: "pointer",
  },
  deleteBtn: {
    borderColor: "#cc6666",
    background: "#f5dada",
    color: "#993333",
    fontSize: "1rem",
    lineHeight: 1,
    padding: "2px 8px",
  },
  addBtn: {
    marginTop: "10px",
    width: "100%",
    padding: "8px",
    border: "2px dashed #c9a84c",
    borderRadius: "3px",
    background: "transparent",
    fontFamily: "'Courier New', Courier, monospace",
    fontWeight: "bold",
    fontSize: "0.8rem",
    letterSpacing: "1px",
    color: "#3a3a3a",
    cursor: "pointer",
  },
  editBox: {
    background: "#fefbe8",
    border: "2px solid #c9a84c",
    borderRadius: "4px",
    padding: "12px",
    margin: "4px 0 8px",
  },
  fieldRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "8px",
  },
  fieldLabel: {
    fontWeight: "bold",
    fontSize: "0.75rem",
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: "#3a3a3a",
    width: "80px",
    flexShrink: 0,
    paddingTop: "5px",
  },
  input: {
    flex: 1,
    padding: "4px 8px",
    border: "2px solid #c9a84c",
    borderRadius: "3px",
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: "0.8rem",
    background: "#fff",
    color: "#3a3a3a",
    outline: "none",
  },
  tagsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  tag: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "#2d6363",
    color: "white",
    borderRadius: "3px",
    padding: "2px 6px",
    fontSize: "0.75rem",
    fontWeight: "bold",
  },
  tagRemove: {
    background: "none",
    border: "none",
    color: "rgba(255,255,255,0.8)",
    cursor: "pointer",
    padding: "0",
    fontSize: "0.9rem",
    lineHeight: 1,
  },
  suggestions: {
    marginTop: "4px",
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
  },
  suggestionItem: {
    padding: "2px 8px",
    border: "1px solid #c9a84c",
    borderRadius: "3px",
    background: "#fffdf0",
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: "0.75rem",
    cursor: "pointer",
    color: "#3a3a3a",
  },
  checkRow: {
    marginBottom: "6px",
  },
  checkLabel: {
    display: "flex",
    alignItems: "center",
    fontSize: "0.8rem",
    color: "#3a3a3a",
    cursor: "pointer",
  },
};
